<template>
  <div>
    <v-layout wrap justify-end>
      <v-flex xs12 md12>
        <v-menu
          v-model="menuOpen"
          offset-y
          right
          transition="slide-y-transition"
          :close-on-content-click="false"
          content-class="profile-menu-dropdown"
        >
          <!-- Top Header Activator -->
          <template v-slot:activator="{ attrs, on }">
            <div
              class="user-profile-activator d-flex align-center"
              v-bind="attrs"
              v-on="on"
              :class="{ 'activator-active': menuOpen }"
              title="User Account"
            >
              <div class="avatar-circle">
                {{ userInitial }}
              </div>
              <div class="user-info text-left ml-2 d-none d-sm-flex flex-column">
                <span class="user-name">{{ displayName }}</span>
                <span class="user-role">{{ displayRole }}</span>
              </div>
              <v-icon
                small
                color="#8c6a43"
                class="ml-1 chevron-icon"
                :class="{ 'chevron-rotated': menuOpen }"
              >
                mdi-chevron-down
              </v-icon>
            </div>
          </template>

          <!-- Redesigned Luxury Profile Dropdown Card -->
          <v-card class="luxury-profile-card">
            <!-- Decorative Top Banner -->
            <div class="profile-header-banner">
              <div class="banner-gold-decor">
                <v-icon size="16" color="#b9893f">mdi-octagram-outline</v-icon>
              </div>
            </div>

            <!-- Profile Info Section (overlapping banner) -->
            <div class="profile-avatar-container text-center">
              <div class="profile-main-avatar">
                <span>{{ userInitial }}</span>
                <div class="online-indicator" title="Active"></div>
              </div>

              <!-- Name & Identity -->
              <h3 class="profile-display-name">
                {{ displayName }}
              </h3>

              <!-- Store / Jewellery Name -->
              <div class="profile-store-name" v-if="displayJewelleryName">
                <v-icon size="13" color="#b9893f" class="mr-1">mdi-storefront-outline</v-icon>
                <span>{{ displayJewelleryName }}</span>
              </div>

              <!-- Role Badge -->
              <div class="profile-role-badge">
                <span>{{ displayRole }}</span>
              </div>

              <!-- Email or Tax ID -->
              <div class="profile-email" v-if="displayEmail">
                {{ displayEmail }}
              </div>
            </div>

            <!-- Divider -->
            <div class="profile-menu-divider"></div>

            <!-- Quick Action Items -->
            <div class="profile-menu-items px-3 py-2">
              <!-- My Profile -->
              <div class="profile-action-row" @click="goToProfile" v-if="appLogin">
                <div class="action-icon-box profile-icon-bg">
                  <v-icon size="18" color="#b9893f">mdi-account-circle-outline</v-icon>
                </div>
                <div class="action-text-col">
                  <span class="action-title">My Profile</span>
                  <span class="action-desc">Account details & password</span>
                </div>
                <v-icon size="16" color="#a89a8c" class="action-chevron">mdi-chevron-right</v-icon>
              </div>

              <!-- Change Password -->
              <div class="profile-action-row" @click="openChangePassword" v-if="appLogin">
                <div class="action-icon-box profile-icon-bg">
                  <v-icon size="18" color="#b9893f">mdi-lock-reset</v-icon>
                </div>
                <div class="action-text-col">
                  <span class="action-title">Change Password</span>
                  <span class="action-desc">Update your login password</span>
                </div>
                <v-icon size="16" color="#a89a8c" class="action-chevron">mdi-chevron-right</v-icon>
              </div>

              <!-- Boarding Rates / Business Settings -->
              <div class="profile-action-row" @click="openBoardingRates" v-if="userData && userData.mainRole === 'JewelleryAdmin'">
                <div class="action-icon-box rates-icon-bg">
                  <v-icon size="18" color="#b9893f">mdi-tune-vertical-variant</v-icon>
                </div>
                <div class="action-text-col">
                  <span class="action-title">Boarding Rates</span>
                  <span class="action-desc">Update daily market prices</span>
                </div>
                <v-icon size="16" color="#a89a8c" class="action-chevron">mdi-chevron-right</v-icon>
              </div>
            </div>

            <!-- Divider -->
            <div class="profile-menu-divider"></div>

            <!-- Footer: Sign Out / Sign In Button -->
            <div class="profile-footer-section pa-3">
              <button
                v-if="appLogin"
                class="logout-luxury-btn"
                @click="appLogout"
                title="Sign out of JewelBiz"
              >
                <v-icon size="16" color="#dc2626" class="mr-2">mdi-logout-variant</v-icon>
                <span>Sign Out</span>
              </button>

              <button
                v-else
                class="login-luxury-btn"
                @click="goToLogin"
                title="Sign into JewelBiz"
              >
                <v-icon size="16" color="#c44f0b" class="mr-2">mdi-login</v-icon>
                <span>Sign In</span>
              </button>
            </div>
          </v-card>
        </v-menu>
      </v-flex>
    </v-layout>

    <!-- Change Password Dialog -->
    <v-dialog v-model="changePasswordDialog" max-width="440px" persistent>
      <v-card class="change-password-card pa-6">
        <div class="d-flex align-center justify-space-between mb-4">
          <h3 class="dialog-title-text">Change Password</h3>
          <v-btn icon small text @click="closeChangePasswordDialog">
            <v-icon color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-form ref="changePasswordForm" v-model="changePasswordFormValid">
          <label class="cp-input-label">Current Password</label>
          <v-text-field
            v-model="currentPassword"
            outlined
            dense
            :type="showCurrentPassword ? 'text' : 'password'"
            :append-icon="showCurrentPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="showCurrentPassword = !showCurrentPassword"
            :rules="[cpRules.required]"
            class="cp-input mb-2"
          ></v-text-field>

          <label class="cp-input-label">New Password</label>
          <v-text-field
            v-model="newPassword"
            outlined
            dense
            :type="showNewPassword ? 'text' : 'password'"
            :append-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="showNewPassword = !showNewPassword"
            :rules="[cpRules.required, cpRules.minLength]"
            class="cp-input mb-2"
          ></v-text-field>

          <label class="cp-input-label">Confirm New Password</label>
          <v-text-field
            v-model="confirmPassword"
            outlined
            dense
            :type="showConfirmPassword ? 'text' : 'password'"
            :append-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="showConfirmPassword = !showConfirmPassword"
            :rules="[cpRules.required, cpRules.matches]"
            class="cp-input"
          ></v-text-field>
        </v-form>

        <div class="d-flex align-center justify-end mt-5">
          <button class="cp-cancel-btn mr-3" @click="closeChangePasswordDialog">Cancel</button>
          <button class="cp-save-btn" :disabled="changePasswordLoading" @click="submitChangePassword">
            {{ changePasswordLoading ? "Updating..." : "Update Password" }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackBar" :color="snackColor" right :timeout="3000">
      {{ msg }}
    </v-snackbar>
  </div>
</template>

<script>
import store from "./../store";
import axios from "axios";

export default {
  data() {
    return {
      menuOpen: false,
      user: {},
      taxType: localStorage.getItem("GSTstatus"),
      showSnackBar: false,
      snackColor: "#dc2626",
      msg: "",
      changePasswordDialog: false,
      changePasswordFormValid: true,
      changePasswordLoading: false,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      cpRules: {
        required: (value) => !!value || "Required field.",
        minLength: (value) => (value && value.length >= 6) || "Must be at least 6 characters.",
        matches: (value) => value === this.newPassword || "Passwords do not match.",
      },
    };
  },
  computed: {
    appLogin() {
      return this.$store.state.isLoggedIn;
    },
    appUser() {
      return this.$store.state.email;
    },
    userData() {
      return this.$store.state.userData;
    },
    displayName() {
      if (this.userData && (this.userData.username || this.userData.name || this.userData.fullName)) {
        return this.userData.username || this.userData.name || this.userData.fullName;
      }
      return "Nandhu";
    },
    userInitial() {
      const name = this.displayName;
      return name ? name.charAt(0).toUpperCase() : "N";
    },
    displayRole() {
      const role = this.userData && this.userData.mainRole;
      if (!role) return "Jewellery Admin";
      if (role === "JewelleryAdmin") return "Jewellery Admin";
      if (role === "SuperAdmin") return "Super Admin";
      return role;
    },
    displayJewelleryName() {
      if (this.userData && this.userData.jewelleryName) {
        return this.userData.jewelleryName;
      }
      if (
        this.userData &&
        this.userData.subRoleId &&
        this.userData.subRoleId.jewelleryId &&
        this.userData.subRoleId.jewelleryId.jewelleryName
      ) {
        return this.userData.subRoleId.jewelleryId.jewelleryName;
      }
      return "";
    },
    displayEmail() {
      if (this.userData && this.userData.email) return this.userData.email;
      if (this.appUser && typeof this.appUser === "string") return this.appUser;
      return "";
    },
  },
  methods: {
    goToProfile() {
      this.menuOpen = false;
      if (this.$route.path !== "/userProfile") {
        this.$router.push("/userProfile");
      }
    },
    openBoardingRates() {
      this.menuOpen = false;
      if (this.$route.path === "/JewelleryDashboard") {
        this.$root.$emit("open-boarding-rates-dialog");
      } else {
        this.$router.push("/BoardingRate");
      }
    },
    goToLogin() {
      this.menuOpen = false;
      this.$router.push("/login");
    },
    openChangePassword() {
      this.menuOpen = false;
      this.currentPassword = "";
      this.newPassword = "";
      this.confirmPassword = "";
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      this.showConfirmPassword = false;
      this.changePasswordDialog = true;
      if (this.$refs.changePasswordForm) {
        this.$refs.changePasswordForm.resetValidation();
      }
    },
    closeChangePasswordDialog() {
      this.changePasswordDialog = false;
    },
    submitChangePassword() {
      if (this.$refs.changePasswordForm && !this.$refs.changePasswordForm.validate()) {
        return;
      }
      this.changePasswordLoading = true;
      axios({
        method: "POST",
        url: "/user/change/password",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          password: this.currentPassword,
          newpassword: this.newPassword,
        },
      })
        .then((response) => {
          this.changePasswordLoading = false;
          if (response.data.status) {
            this.snackColor = "#16a34a";
            this.msg = response.data.msg || "Password updated successfully";
            this.showSnackBar = true;
            this.changePasswordDialog = false;
          } else {
            this.snackColor = "#dc2626";
            this.msg = response.data.msg || "Could not update password";
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.changePasswordLoading = false;
          this.snackColor = "#dc2626";
          this.msg = (err.response && err.response.data && err.response.data.msg) || "Could not update password";
          this.showSnackBar = true;
        });
    },
    appLogout() {
      this.menuOpen = false;
      axios({
        method: "get",
        url: "/user/logout",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.$store.commit("logoutUser", true);
            localStorage.removeItem("id");
            localStorage.removeItem("token");
            this.$router.push("/login");
          } else {
            this.msg = response.data.msg || "Logout failed";
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          store.commit("appLoading", false);
          console.log(err);
          // Fallback logout even on network error
          this.$store.commit("logoutUser", true);
          localStorage.removeItem("id");
          localStorage.removeItem("token");
          this.$router.push("/login");
        });
    },
  },
};
</script>

<style scoped>
/* Activator in Top Header */
.user-profile-activator {
  cursor: pointer;
  padding: 3px 8px 3px 4px;
  border-radius: 24px;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  user-select: none;
}

.user-profile-activator:hover,
.activator-active {
  background-color: #fff4e8;
  border-color: #f3dec9;
}

.avatar-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #c44f0b 0%, #a13d07 100%);
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(196, 79, 11, 0.25);
}

.user-name {
  font-family: 'Inter', sans-serif;
  font-size: 13.5px;
  font-weight: 700;
  color: #172238;
  line-height: 1.2;
  text-transform: capitalize;
}

.user-role {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #8c6a43;
  line-height: 1.2;
}

.chevron-icon {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.chevron-rotated {
  transform: rotate(180deg);
}

/* Luxury Dropdown Card */
.luxury-profile-card {
  width: 285px;
  border-radius: 16px !important;
  background: #ffffff !important;
  border: 1px solid #ebd8c2 !important;
  box-shadow: 0 16px 40px rgba(78, 43, 14, 0.14), 0 4px 12px rgba(0, 0, 0, 0.05) !important;
  overflow: hidden;
}

/* Header Banner */
.profile-header-banner {
  height: 58px;
  background: linear-gradient(135deg, #fdf5ea 0%, #faecd9 50%, #f6deb8 100%);
  position: relative;
}

.banner-gold-decor {
  position: absolute;
  top: 8px;
  right: 12px;
  opacity: 0.8;
}

/* Overlapping Avatar & Identity */
.profile-avatar-container {
  padding: 0 16px 10px;
  margin-top: -30px;
}

.profile-main-avatar {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: linear-gradient(135deg, #c44f0b 0%, #9a3c06 100%);
  border: 3px solid #ffffff;
  box-shadow: 0 4px 12px rgba(196, 79, 11, 0.28);
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-weight: 800;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 8px;
  position: relative;
}

.online-indicator {
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #16a34a;
  border: 2px solid #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.profile-display-name {
  font-family: 'Inter', sans-serif;
  font-size: 15.5px;
  font-weight: 800;
  color: #172238;
  margin: 0;
  line-height: 1.2;
  text-transform: capitalize;
}

.profile-store-name {
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #8c6a43;
  margin-top: 3px;
  text-transform: capitalize;
}

.profile-role-badge {
  display: inline-block;
  background: #fff4e8;
  border: 1px solid #f3dec9;
  border-radius: 20px;
  padding: 2px 10px;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: #c44f0b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 6px;
}

.profile-email {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: #8c827a;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Dividers */
.profile-menu-divider {
  height: 1px;
  background: #f4e8dc;
  margin: 0 12px;
}

/* Action Rows */
.profile-menu-items {
  display: flex;
  flex-direction: column;
}

.profile-action-row {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  margin-bottom: 2px;
  user-select: none;
}

.profile-action-row:hover {
  background: #fff6ec;
}

.profile-action-row:hover .action-title {
  color: #c44f0b;
}

.profile-action-row:hover .action-chevron {
  color: #c44f0b;
  transform: translateX(2px);
}

.action-icon-box {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 10px;
}

.profile-icon-bg {
  background: #fff0df;
  border: 1px solid #fce3ca;
}

.rates-icon-bg {
  background: #fbf5eb;
  border: 1px solid #f2e3cd;
}

.action-text-col {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  text-align: left;
}

.action-title {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #172238;
  line-height: 1.2;
  transition: color 0.2s ease;
}

.action-desc {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px;
  color: #8c827a;
  line-height: 1.1;
  margin-top: 2px;
}

.action-chevron {
  transition: transform 0.2s ease, color 0.2s ease;
}

/* Footer Buttons */
.logout-luxury-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #dc2626;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.logout-luxury-btn:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  box-shadow: 0 2px 6px rgba(220, 38, 38, 0.12);
}

.login-luxury-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  background: #fff4e8;
  border: 1px solid #f3dec9;
  border-radius: 10px;
  color: #c44f0b;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.login-luxury-btn:hover {
  background: #fae2c5;
  border-color: #e5b98a;
}

/* Change Password Dialog */
.change-password-card {
  border-radius: 16px !important;
  background: #ffffff !important;
}

.dialog-title-text {
  font-family: 'Inter', sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: #172238;
  margin: 0;
}

.cp-input-label {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
}

.cp-cancel-btn {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.cp-cancel-btn:hover {
  background: #e2e8f0;
}

.cp-save-btn {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  border: none;
  border-radius: 10px;
  padding: 8px 22px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28);
}

.cp-save-btn:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38);
  transform: translateY(-1px);
}

.cp-save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
